import { WHOOP_API_BASE } from '@/auth/config'
import { refreshIfNeeded } from '@/auth/oauth'
import type {
  Cycle,
  Page,
  Recovery,
  Sleep,
  UserBasicProfile,
  UserBodyMeasurement,
  Workout,
} from './types'

export class AuthError extends Error {}
export class RateLimitError extends Error {
  constructor(public retryAfterSeconds: number) {
    super(`Rate limited; retry after ${retryAfterSeconds}s`)
  }
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const tokens = await refreshIfNeeded()
  if (!tokens) throw new AuthError('Not logged in')

  const url = path.startsWith('http') ? path : `${WHOOP_API_BASE}${path}`
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `${tokens.token_type} ${tokens.access_token}`,
      Accept: 'application/json',
    },
  })

  if (res.status === 401) throw new AuthError('Whoop returned 401')
  if (res.status === 429) {
    const retry = parseInt(res.headers.get('Retry-After') ?? '5', 10)
    throw new RateLimitError(retry)
  }
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    throw new Error(`Whoop API ${res.status} ${res.statusText}: ${txt}`)
  }
  return res
}

async function getJson<T>(path: string): Promise<T> {
  const res = await authedFetch(path)
  return (await res.json()) as T
}

interface ListParams {
  start?: string // ISO datetime
  end?: string // ISO datetime
  limit?: number // default 25, max 25
  nextToken?: string
}

function buildQuery(p: ListParams): string {
  const sp = new URLSearchParams()
  if (p.start) sp.set('start', p.start)
  if (p.end) sp.set('end', p.end)
  if (p.limit) sp.set('limit', String(p.limit))
  if (p.nextToken) sp.set('nextToken', p.nextToken)
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/**
 * Iterate every page of a paginated list endpoint, yielding records.
 * Caller should consume with `for await`.
 */
export async function* paginate<T>(
  path: string,
  params: ListParams = {},
): AsyncIterable<T> {
  let nextToken: string | undefined = undefined
  do {
    const page: Page<T> = await getJson<Page<T>>(path + buildQuery({ ...params, nextToken }))
    for (const r of page.records) yield r
    nextToken = page.next_token
  } while (nextToken)
}

// --- Endpoint wrappers ---

export const api = {
  cycles: (p: ListParams = {}) => paginate<Cycle>('/v2/cycle', { limit: 25, ...p }),
  recoveries: (p: ListParams = {}) => paginate<Recovery>('/v2/recovery', { limit: 25, ...p }),
  sleep: (p: ListParams = {}) => paginate<Sleep>('/v2/activity/sleep', { limit: 25, ...p }),
  workouts: (p: ListParams = {}) => paginate<Workout>('/v2/activity/workout', { limit: 25, ...p }),
  profile: () => getJson<UserBasicProfile>('/v2/user/profile/basic'),
  body: () => getJson<UserBodyMeasurement>('/v2/user/measurement/body'),
}
